import React, { useEffect, useState } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle2, X } from 'lucide-react';
import useSEO from '../hooks/useSEO';
import { SITE } from '../content/site';
import { alternatesOf } from '../content';

// Clé de site reCAPTCHA v3 (publique) ; la clé secrète est dans server/.env.
const RECAPTCHA_SITE_KEY = '6LdHFdYtAAAAANwq19j0C3LehqWwSoz03SUwVcBy';

const inputClasses =
  'w-full px-4 py-3 bg-white border border-line focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-colors text-ink placeholder:text-ink-faint';

// Textes de la page, par langue. Le sujet et l'urgence sont transmis en
// français dans l'e-mail (subjectFr, urgencyFr), avec la langue du formulaire.
const TEXT = {
  fr: {
    path: '/contact',
    title: "Contact & Demande d'information – Expomis Luxembourg",
    description: "Contactez Expomis pour une demande d'information ou une intervention d'urgence. Disponible 24h/24 pour les sinistres urgents au Luxembourg.",
    h1: 'Contactez-nous',
    intro: "Vous avez des questions ou besoin d'un diagnostic rapide ? Notre équipe est à votre écoute.",
    details: 'Nos coordonnées',
    phone: 'Téléphone',
    phoneNote: 'Disponible 24h/24 pour les urgences',
    email: 'Email',
    emailNote: 'Réponse sous 2h en moyenne',
    address: 'Adresse',
    area: 'Tout le Grand-Duché et le sud de la province de Luxembourg (Belgique)',
    hoursTitle: 'Horaires',
    hours: ['Lun-Ven : 9h-18h', 'Sam : 9h-16h'],
    hoursNote: "Service d'urgence 24h/24",
    emergencyTitle: "Intervention d'urgence",
    emergencyText: "En cas de fuite importante ou de sinistre urgent, contactez-nous immédiatement. Notre équipe peut intervenir dans l'heure qui suit votre appel.",
    emergencyLabel: 'Urgence :',
    formTitle: "Demande d'information",
    success: 'Votre message a été envoyé avec succès !',
    error: "Une erreur s'est produite. Veuillez réessayer.",
    required: 'Veuillez remplir tous les champs obligatoires',
    name: 'Nom complet',
    namePlaceholder: 'Votre nom et prénom',
    emailPlaceholder: 'votre@email.com',
    urgency: 'Urgence',
    urgencies: {
      normal: 'Normal (sous 48h)',
      urgent: 'Urgent (sous 24h)',
      emergency: 'Urgence (immédiat)',
    },
    subject: 'Sujet',
    subjectPlaceholder: 'Sélectionnez un service',
    subjects: {
      'detection-fuite': 'Détection de fuite',
      'contre-expertise': 'Contre-expertise',
      'expertise-incendie': 'Expertise incendie',
      'expertise-vol': 'Expertise vol',
      'expertise-tempete': 'Expertise tempête',
      'degats-eaux': 'Dégâts des eaux',
      'responsabilite-civile': 'Responsabilité civile',
      autre: 'Autre demande',
    },
    message: 'Message',
    messagePlaceholder: 'Décrivez votre problème ou votre demande en détail...',
    noticeTitle: 'Information importante :',
    noticeBefore: "en cas d'urgence (fuite importante, sinistre en cours), appelez-nous directement au",
    noticeAfter: 'pour une intervention immédiate.',
    sending: 'Envoi en cours...',
    send: 'Envoyer ma demande',
    faqTitle: 'Questions fréquentes',
    faq: [
      {
        question: 'Combien coûte une détection de fuite ?',
        answer: "Chaque prestation est facturée à un prix fixé avec vous avant l'intervention, selon la complexité de la recherche ; si la fuite est trouvée en une seule visite sans démontage, un forfait s'applique lorsqu'il vous est plus favorable. Dans la majorité des cas, notre prestation est remboursée par votre assurance."
      },
      {
        question: 'Intervenez-vous le weekend ?',
        answer: "Oui, nous intervenons le samedi et proposons un service d'urgence 24h/24 pour les cas critiques."
      },
      {
        question: 'Vos méthodes endommagent-elles les installations ?',
        answer: "Non, nous utilisons exclusivement des méthodes non destructives qui préservent l'intégrité de vos installations."
      },
      {
        question: 'Sous quel délai recevons-nous le rapport ?',
        answer: 'Vous recevez un rapport complet et détaillé, illustré de photos et mesures, sous 72h maximum.'
      },
      {
        question: "Faire appel à un contre-expert, est-ce un signe de conflit avec mon assureur ?",
        answer: "Non, c'est un droit prévu par votre contrat d'assurance. Un contre-expert indépendant défend vos intérêts face à l'expert de la compagnie, sans confrontation."
      },
      {
        question: "Combien coûte une contre-expertise ?",
        answer: "Les frais de contre-expertise sont souvent couverts, en tout ou partie, par votre contrat d'assurance (garantie protection juridique ou clause dédiée). Nous restons disponibles pour en discuter avec vous sans engagement."
      }
    ],
  },
  de: {
    path: '/de/kontakt',
    title: 'Kontakt & Anfrage – Expomis Luxemburg',
    description: 'Kontaktieren Sie Expomis für eine Anfrage zur Leckortung oder einen Noteinsatz. Notdienst rund um die Uhr bei dringenden Schäden in Luxemburg.',
    h1: 'Kontakt',
    intro: 'Sie haben Fragen oder brauchen schnell eine Diagnose? Wir sind für Sie da.',
    details: 'Unsere Kontaktdaten',
    phone: 'Telefon',
    phoneNote: 'Im Notfall rund um die Uhr erreichbar',
    email: 'E-Mail',
    emailNote: 'Antwort im Durchschnitt innerhalb von 2 Stunden',
    address: 'Adresse',
    area: 'Ganzes Großherzogtum und Süden der belgischen Provinz Luxemburg',
    hoursTitle: 'Öffnungszeiten',
    hours: ['Mo-Fr: 9-18 Uhr', 'Sa: 9-16 Uhr'],
    hoursNote: 'Notdienst rund um die Uhr',
    emergencyTitle: 'Noteinsatz',
    emergencyText: 'Bei einem größeren Leck oder einem akuten Schaden rufen Sie uns sofort an. Wir können innerhalb einer Stunde nach Ihrem Anruf vor Ort sein.',
    emergencyLabel: 'Notfall:',
    formTitle: 'Anfrage',
    success: 'Ihre Nachricht wurde gesendet.',
    error: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    required: 'Bitte füllen Sie alle Pflichtfelder aus.',
    name: 'Vor- und Nachname',
    namePlaceholder: 'Ihr Name',
    emailPlaceholder: 'ihre@email.lu',
    urgency: 'Dringlichkeit',
    urgencies: {
      normal: 'Normal (innerhalb von 48 Std.)',
      urgent: 'Dringend (innerhalb von 24 Std.)',
      emergency: 'Notfall (sofort)',
    },
    subject: 'Betreff',
    subjectPlaceholder: 'Leistung auswählen',
    subjects: {
      'detection-fuite': 'Leckortung',
      'contre-expertise': 'Gegengutachten',
      'expertise-incendie': 'Gutachten Brandschaden',
      'expertise-vol': 'Gutachten Einbruch und Diebstahl',
      'expertise-tempete': 'Gutachten Sturmschaden',
      'degats-eaux': 'Wasserschaden',
      'responsabilite-civile': 'Haftpflicht',
      autre: 'Sonstige Anfrage',
    },
    message: 'Nachricht',
    messagePlaceholder: 'Beschreiben Sie Ihr Problem oder Ihre Anfrage möglichst genau...',
    noticeTitle: 'Wichtig:',
    noticeBefore: 'Im Notfall (größeres Leck, laufender Schaden) rufen Sie uns direkt unter',
    noticeAfter: 'an, damit wir sofort eingreifen können.',
    sending: 'Wird gesendet...',
    send: 'Anfrage senden',
    faqTitle: 'Häufige Fragen',
    faq: [
      {
        question: 'Was kostet eine Leckortung?',
        answer: 'Jede Leistung wird zu einem Preis abgerechnet, der vor dem Einsatz mit Ihnen festgelegt wird, je nach Aufwand der Ortung; wird das Leck bei einem einzigen Besuch ohne Abbau gefunden, gilt eine Pauschale, wenn sie für Sie günstiger ist. In den meisten Fällen übernimmt Ihre Versicherung unsere Leistung.'
      },
      {
        question: 'Kommen Sie auch am Wochenende?',
        answer: 'Ja, wir arbeiten auch samstags, und für dringende Fälle gibt es einen Notdienst rund um die Uhr.'
      },
      {
        question: 'Beschädigen Ihre Verfahren die Installationen?',
        answer: 'Nein, wir arbeiten ausschließlich mit zerstörungsfreien Verfahren, die Ihre Installationen schonen.'
      },
      {
        question: 'Wann erhalten wir den Bericht?',
        answer: 'Sie erhalten innerhalb von höchstens 72 Stunden einen vollständigen, ausführlichen Bericht mit Fotos und Messwerten.'
      },
      {
        question: 'Ist ein Gegengutachter ein Zeichen für einen Streit mit meiner Versicherung?',
        answer: 'Nein, das ist ein Recht, das Ihr Versicherungsvertrag vorsieht. Ein unabhängiger Gegengutachter vertritt Ihre Interessen gegenüber dem Gutachter der Versicherung, ohne Konfrontation.'
      },
      {
        question: 'Was kostet ein Gegengutachten?',
        answer: 'Die Kosten eines Gegengutachtens sind oft ganz oder teilweise durch Ihren Versicherungsvertrag gedeckt (Rechtsschutz oder eine eigene Klausel). Wir besprechen das gerne unverbindlich mit Ihnen.'
      }
    ],
  },
  en: {
    path: '/en/contact',
    title: 'Contact & Enquiries – Expomis Luxembourg',
    description: 'Contact Expomis about leak detection or an emergency call-out. 24/7 emergency service for urgent water damage in Luxembourg.',
    h1: 'Contact us',
    intro: 'Have a question or need a quick diagnosis? Our team is here to help.',
    details: 'Our details',
    phone: 'Phone',
    phoneNote: 'Available 24/7 for emergencies',
    email: 'Email',
    emailNote: 'We usually reply within 2 hours',
    address: 'Address',
    area: 'The whole Grand Duchy and the south of the Belgian province of Luxembourg',
    hoursTitle: 'Opening hours',
    hours: ['Mon-Fri: 9am-6pm', 'Sat: 9am-4pm'],
    hoursNote: '24/7 emergency service',
    emergencyTitle: 'Emergency call-out',
    emergencyText: 'If you have a major leak or ongoing damage, call us straight away. We can be on site within an hour of your call.',
    emergencyLabel: 'Emergency:',
    formTitle: 'Send an enquiry',
    success: 'Your message has been sent.',
    error: 'Something went wrong. Please try again.',
    required: 'Please fill in all required fields.',
    name: 'Full name',
    namePlaceholder: 'Your first and last name',
    emailPlaceholder: 'you@example.com',
    urgency: 'Urgency',
    urgencies: {
      normal: 'Normal (within 48 hours)',
      urgent: 'Urgent (within 24 hours)',
      emergency: 'Emergency (immediately)',
    },
    subject: 'Subject',
    subjectPlaceholder: 'Choose a service',
    subjects: {
      'detection-fuite': 'Leak detection',
      'contre-expertise': 'Independent counter-assessment',
      'expertise-incendie': 'Fire damage assessment',
      'expertise-vol': 'Burglary and theft assessment',
      'expertise-tempete': 'Storm damage assessment',
      'degats-eaux': 'Water damage',
      'responsabilite-civile': 'Third-party liability',
      autre: 'Other enquiry',
    },
    message: 'Message',
    messagePlaceholder: 'Describe your problem or request in as much detail as you can...',
    noticeTitle: 'Important:',
    noticeBefore: 'in an emergency (major leak, damage in progress), call us directly on',
    noticeAfter: 'so we can come out straight away.',
    sending: 'Sending...',
    send: 'Send my enquiry',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'How much does leak detection cost?',
        answer: 'Each job is charged at a price agreed with you before we start, depending on how complex the search is; if the leak is found in a single visit without taking anything apart, a flat rate applies when it is cheaper for you. In most cases, your insurance pays for our service.'
      },
      {
        question: 'Do you work at weekends?',
        answer: 'Yes, we work on Saturdays and run a 24/7 emergency service for critical cases.'
      },
      {
        question: 'Will your methods damage my installations?',
        answer: 'No, we only use non-destructive methods that leave your installations intact.'
      },
      {
        question: 'How soon do we get the report?',
        answer: 'You receive a full, detailed report with photos and measurements within 72 hours at most.'
      },
      {
        question: 'Does calling in a counter-assessor mean I am in dispute with my insurer?',
        answer: "No, it is a right provided for in your insurance policy. An independent assessor defends your interests in dealing with the insurer's assessor, without confrontation."
      },
      {
        question: 'How much does a counter-assessment cost?',
        answer: 'Counter-assessment fees are often covered, in full or in part, by your insurance policy (legal expenses cover or a specific clause). We are happy to discuss it with you, with no obligation.'
      }
    ],
  },
};

const LANG_TAG = { fr: '', de: ' [formulaire DE]', en: ' [formulaire EN]' };

const Contact = ({ lang = 'fr' }) => {
  const t = TEXT[lang];
  useSEO({
    title: t.title,
    description: t.description,
    path: t.path,
    lang,
    alternates: alternatesOf(t.path)
  });

  // reCAPTCHA n'est utile qu'ici : ne pas le charger sur les autres pages,
  // ni pendant le pré-rendu (son iframe serait figée dans le HTML statique).
  useEffect(() => {
    if (navigator.userAgent === 'ReactSnap') return;
    if (document.getElementById('recaptcha-script')) return;
    const script = document.createElement('script');
    script.id = 'recaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    document.head.appendChild(script);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    urgency: 'normal'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      alert(t.required);
      return;
    }

    setIsSubmitting(true);

    try {
      const recaptchaToken = await window.grecaptcha.execute(
        RECAPTCHA_SITE_KEY,
        { action: 'submit' }
      );

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          gsm: formData.phone,
          subject: `${TEXT.fr.subjects[formData.subject]}${LANG_TAG[lang]}`,
          message: `Urgence : ${TEXT.fr.urgencies[formData.urgency]}\n\n${formData.message}`,
          recaptcha_token: recaptchaToken
        })
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          urgency: 'normal'
        });
      } else {
        console.error(result);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error(error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="pt-40 pb-24">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-20">
          <div className="text-xs font-semibold tracking-widest uppercase text-brand mb-4">
            Contact
          </div>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-ink mb-6">
            {t.h1}
          </h1>
          <p className="text-lg text-ink-soft leading-relaxed">
            {t.intro}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl">
          {/* Informations de contact */}
          <div>
            <h2 className="font-serif text-2xl font-semibold text-ink mb-8">{t.details}</h2>

            <div className="space-y-7 mb-10">
              <div className="flex items-start gap-4 border-t border-line pt-5">
                <Phone className="w-5 h-5 text-brand mt-0.5" strokeWidth={1.6} />
                <div>
                  <h3 className="font-semibold text-ink">{t.phone}</h3>
                  <p className="text-ink-soft"><a href={SITE.phoneHref} className="hover:text-ink">{SITE.phone}</a></p>
                  <p className="text-sm text-ink-faint">{t.phoneNote}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 border-t border-line pt-5">
                <Mail className="w-5 h-5 text-brand mt-0.5" strokeWidth={1.6} />
                <div>
                  <h3 className="font-semibold text-ink">{t.email}</h3>
                  <p className="text-ink-soft"><a href={`mailto:${SITE.email}`} className="hover:text-ink">{SITE.email}</a></p>
                  <p className="text-sm text-ink-faint">{t.emailNote}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 border-t border-line pt-5">
                <MapPin className="w-5 h-5 text-brand mt-0.5" strokeWidth={1.6} />
                <div>
                  <h3 className="font-semibold text-ink">{t.address}</h3>
                  <p className="text-ink-soft">{SITE.address}</p>
                  <p className="text-sm text-ink-faint">{t.area}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 border-t border-line pt-5">
                <Clock className="w-5 h-5 text-brand mt-0.5" strokeWidth={1.6} />
                <div>
                  <h3 className="font-semibold text-ink">{t.hoursTitle}</h3>
                  {t.hours.map((h) => <p key={h} className="text-ink-soft">{h}</p>)}
                  <p className="text-sm text-ink-faint">{t.hoursNote}</p>
                </div>
              </div>
            </div>

            <div className="bg-ink text-white p-7">
              <h3 className="font-serif text-xl font-semibold mb-3">{t.emergencyTitle}</h3>
              <p className="text-white/70 leading-relaxed mb-4 text-sm">
                {t.emergencyText}
              </p>
              <a href={SITE.phoneHref} className="flex items-center gap-2 text-brand-cyan font-semibold">
                <Phone className="w-4 h-4" />
                <span>{`${t.emergencyLabel} ${SITE.phone}`}</span>
              </a>
            </div>
          </div>

          {/* Formulaire de contact */}
          <div className="bg-white border border-line p-8 md:p-10">
            <h2 className="font-serif text-2xl font-semibold text-ink mb-8">{t.formTitle}</h2>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-white border-l-2 border-brand">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-brand" strokeWidth={1.6} />
                  <span className="text-ink font-medium">{t.success}</span>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-white border-l-2 border-red-500">
                <div className="flex items-center gap-2">
                  <X className="w-5 h-5 text-red-500" />
                  <span className="text-ink font-medium">{t.error}</span>
                </div>
              </div>
            )}

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-semibold text-ink mb-2">
                    {t.name} *
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={inputClasses}
                    placeholder={t.namePlaceholder}
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-semibold text-ink mb-2">
                    {t.email} *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClasses}
                    placeholder={t.emailPlaceholder}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-semibold text-ink mb-2">
                    {t.phone}
                  </label>
                  <input
                    id="contact-phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClasses}
                    placeholder="+352 XX XX XX XX"
                  />
                </div>

                <div>
                  <label htmlFor="contact-urgency" className="block text-sm font-semibold text-ink mb-2">
                    {t.urgency}
                  </label>
                  <select
                    id="contact-urgency"
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleChange}
                    className={inputClasses}
                  >
                    {Object.entries(t.urgencies).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-semibold text-ink mb-2">
                  {t.subject} *
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={inputClasses}
                >
                  <option value="">{t.subjectPlaceholder}</option>
                  {Object.entries(t.subjects).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-semibold text-ink mb-2">
                  {t.message} *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className={`${inputClasses} resize-none`}
                  placeholder={t.messagePlaceholder}
                />
              </div>

              <div className="bg-paper border border-line p-4 text-sm text-ink-soft">
                <strong className="text-ink">{t.noticeTitle}</strong> {t.noticeBefore}{' '}
                <strong className="text-ink">{SITE.phone}</strong>{' '}{t.noticeAfter}
              </div>

              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full py-4 px-6 bg-ink text-white font-medium hover:bg-brand-deep transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t.sending : t.send}
              </button>
            </div>
          </div>
        </div>

        {/* Section FAQ */}
        <div className="mt-28">
          <h2 className="font-serif text-3xl font-semibold text-ink mb-12 text-center">{t.faqTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10 max-w-4xl mx-auto">
            {t.faq.map((faq, index) => (
              <div key={index} className="border-t-2 border-brand pt-5">
                <h3 className="font-semibold text-ink mb-2">{faq.question}</h3>
                <p className="text-ink-soft leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
